<template>
  <div class="qty-selector">
    <div class="label" :class="{ disabled, 'in-stock': inStock }" v-if="label">{{ label }}</div>
    <div>
      <button class="qty-control minus" @click="$emit('reduce-qty')"
        :class="{ disabled: quantity - quantityStep < minQuantity || disabled }">
        <i class="fa-light fa-sharp fa-minus"></i>
      </button>

      <input v-if="typeable" type="number" class="qty" :value="localQuantity" @input="handleInput" @change="handleChange" @blur="handleBlur" max="99" min="0"
        :disabled="disabled" ref="inputRef" />
      <input v-else type="number" class="qty no-typing" :value="quantity" max="99" min="0" disabled />

      <button class="qty-control plus" :class="{ disabled }" @click="$emit('increase-qty')">
        <i class="fa-light fa-sharp fa-plus"></i>
      </button>
    </div>

    <slot></slot>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  label: String,
  quantity: {
    type: Number,
    required: true
  },
  disabled: Boolean,
  inStock: {
    type: Boolean,
    default: false
  },
  minQuantity: {
    type: Number,
    default: 0
  },
  quantityStep: {
    type: Number,
    default: 1
  },
  typeable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['increase-qty', 'reduce-qty', 'set-quantity']);

// Template refs
const inputRef = ref(null);

// Local quantity that user can type in freely
const localQuantity = ref(props.quantity);

// Track if user is actively typing
let isTyping = false;

// Watch for external changes to quantity prop (only update if not typing)
watch(() => props.quantity, (newValue) => {
  if (!isTyping) {
    localQuantity.value = newValue;
  }
});

// Validate and normalize the quantity value based on MOQ and step
function validateQuantity(rawValue) {
  const numValue = parseInt(rawValue, 10);
  if (isNaN(numValue) || numValue <= 0) {
    return 0;
  }
  if (numValue < props.minQuantity) {
    return props.minQuantity;
  }
  // Round to nearest step
  return Math.round(numValue / props.quantityStep) * props.quantityStep;
}

// Handle input events (while typing)
function handleInput(event) {
  const rawValue = event.target.value;
  const numValue = parseInt(rawValue, 10);

  // Clear existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Allow empty string or 0 - user is clearing the field
  if (rawValue === '' || rawValue === '0') {
    localQuantity.value = 0;
    isTyping = true;
    debounceTimer = setTimeout(() => {
      isTyping = false;
      if (props.quantity !== 0) {
        emit('set-quantity', 0);
      }
    }, 500);
    return;
  }

  // If value is a positive number below MOQ, IMMEDIATELY enforce MOQ
  if (!isNaN(numValue) && numValue > 0 && numValue < props.minQuantity) {
    const correctedValue = props.minQuantity;
    localQuantity.value = correctedValue;
    // Force the input element to show corrected value immediately
    event.target.value = correctedValue;
    isTyping = false;
    if (correctedValue !== props.quantity) {
      emit('set-quantity', correctedValue);
    }
    return;
  }

  // Value is valid (>= MOQ), allow it and debounce the step rounding
  localQuantity.value = numValue;
  isTyping = true;

  debounceTimer = setTimeout(() => {
    const validatedValue = validateQuantity(numValue);
    localQuantity.value = validatedValue;
    if (inputRef.value) {
      inputRef.value.value = validatedValue;
    }
    isTyping = false;
    if (validatedValue !== props.quantity) {
      emit('set-quantity', validatedValue);
    }
  }, 500);
}

// Handle change events (when user stops typing/editing)
function handleChange(event) {
  // Clear debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  const validatedValue = validateQuantity(event.target.value);
  localQuantity.value = validatedValue;
  event.target.value = validatedValue;
  isTyping = false;

  if (validatedValue !== props.quantity) {
    emit('set-quantity', validatedValue);
  }
}

// Handle blur events (when user clicks away) - ALWAYS enforce validation
function handleBlur(event) {
  // Clear debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Always validate and correct on blur - this is the final enforcement
  const rawValue = event.target.value;
  const validatedValue = validateQuantity(rawValue);

  // Force update both the local state and the DOM element
  localQuantity.value = validatedValue;
  event.target.value = validatedValue;
  isTyping = false;

  // Always emit to ensure parent is in sync
  emit('set-quantity', validatedValue);
}

// Debounce timer
let debounceTimer = null;

// Cleanup on unmount
onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});
</script>
